import * as React from "react";

import { BreadcrumbSpec } from "@impero-com/common/componentTypes/breadcrumb";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { ActionSpec } from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import { UserGroupView } from "api/generated/model";
import { HashSwitch } from "org/router";
import { FormCreate, FormRootComponentProps } from "utils/form";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { WebRoute } from "../../../routes";

import { UserGroupDetails } from "./details";
import { UserGroupDetailsMembers } from "./members";

export const USER_GROUP_DETAILS_TAB = "USER_GROUP_DETAILS";
export const USER_GROUP_MEMBERS_TAB = "USER_GROUP_MEMBERS";

type Tab = typeof USER_GROUP_DETAILS_TAB | typeof USER_GROUP_MEMBERS_TAB;

export const MEMBERS_HASH = "#members";

interface UserGroupsApplicationBarProps {
    currentTabId: Tab;
    actions?: ActionSpec[];

    // userGroup may be null when non existing user group is navigated
    userGroup?: UserGroupView | null;
    children: React.ReactNode;
}

interface AppBarProps {
    ajaxManager: AjaxManager;
}

interface UserGroupManagerProps
    extends FormRootComponentProps<object>, AppBarProps {}

function InnerUserGroupManager(props: UserGroupManagerProps) {
    const { ajaxManager } = props;

    const detailsTab = <UserGroupDetails ajaxManager={ajaxManager} />;

    const membersTab = <UserGroupDetailsMembers ajaxManager={ajaxManager} />;

    return (
        <HashSwitch
            hashRoutes={{
                "": detailsTab,
                [MEMBERS_HASH]: membersTab,
            }}
        />
    );
}

export function UserGroupsApplicationBar({
    userGroup = null,
    actions = [],
    currentTabId,
    children,
}: UserGroupsApplicationBarProps) {
    const navigate = useNavigate();

    // Build title and breadcrumbs
    const buildTitle = (): BreadcrumbSpec => {
        if (userGroup) {
            return {
                title: userGroup.name,
                breadCrumbs: [
                    {
                        label: t("UserManagement_ModuleTitle"),
                        url: WebRoute.USERS,
                    },
                    {
                        label: t("UserManagement_Groups"),
                        url: WebRoute.USER_GROUPS,
                    },
                ],
            };
        } else {
            return {
                title: t("UserManagement_Groups"),
                breadCrumbs: [
                    {
                        label: t("UserManagement_ModuleTitle"),
                        url: WebRoute.USERS,
                    },
                ],
            };
        }
    };

    return (
        <OrgApplicationBar
            title={buildTitle()}
            actions={{
                id: "userlist-actions",
                actions,
            }}
            navigation={{
                type: "Tabs",
                currentTabId,
                tabSpecs: [
                    {
                        id: USER_GROUP_DETAILS_TAB,
                        label: t("UserManagement_Groups_DetailsTab"),
                        onClick: () => {
                            navigate("#");
                        },
                    },
                    {
                        id: USER_GROUP_MEMBERS_TAB,
                        label: t("UserManagement_Groups_MembersTab"),
                        onClick: () => {
                            navigate(MEMBERS_HASH);
                        },
                    },
                ],
            }}
        >
            {children}
        </OrgApplicationBar>
    );
}

export const UserGroupManager = FormCreate<object, UserGroupManagerProps>()(
    withI18n<UserGroupManagerProps>(InnerUserGroupManager)
);
